import {
  createInternalCategory,
  updateInternalCategory,
  useInternalCategories,
  useInternalCategory,
  useProducts,
} from '@gozoki/api'
import {
  useInternalCategoryForm,
  type InternalCategoryForm,
} from '@gozoki/api/src/forms/useInternalCategoryForm'
import { Button, IconButton, Typography } from '@mui/material'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { AxiosError } from 'axios'
import { ArrowLeft } from 'lucide-react'
import { useEffect, useMemo } from 'react'
import { useController, useWatch } from 'react-hook-form'
import z from 'zod/v3'
import LoadingPaper from '../../../components/containers/LoadingPaper'
import ProductDualListSelectorRhf, {
  type ProductDualListItem,
} from '../../../components/inputs/ProductDualListSelectorRhf'
import TextFieldRhf from '../../../components/inputs/TextFieldRhf'
import Page from '../../../components/Page'
import { Label } from '../../../components/ui/label'
import { Switch } from '../../../components/ui/switch'
import { useRights } from '../../../utils/hooks'
import { ThemeColors } from '../../../utils/theme/colors.theme'
import { fonts } from '../../../utils/theme/fonts.theme'
import { ButtonOverrides } from '../../../utils/theme/overrides.theme'

import { withResourcePermission } from '../../../components/RequireResourcePermission'
const DUPLICATE_INTERNAL_CATEGORY_NAME_MESSAGE =
  'Veuillez choisir un nouveau nom, une catégorie interne porte déjà ce nom.'

const normalizeCategoryName = (value: string) => value.trim().toLocaleLowerCase('fr')

export const Route = createFileRoute('/_authenticated/categories/internal/new')({
  component: withResourcePermission('categories', 'read')(AddInternalCategoryPage),
  validateSearch: z.object({
    internalCategoryId: z.number().optional(),
  }),
})

function AddInternalCategoryPage() {
  const navigate = Route.useNavigate()
  const queryClient = useQueryClient()
  const { internalCategoryId } = Route.useSearch()

  const isUserAdmin = useRights(['ADMIN'])
  const updateMode = internalCategoryId !== undefined // Update mode if there is a category ID in the URL

  // If this is update mode, use isLoading to show a loading indicator before the data arrives
  const { data, isLoading } = useInternalCategory(internalCategoryId)
  const { data: products } = useProducts()
  const { data: internalCategories } = useInternalCategories()

  const {
    handleSubmit,
    control,
    trigger,
    setValue,
    setError,
    clearErrors,
    formState: { errors },
  } = useInternalCategoryForm()
  const watchedProductIds = useWatch({
    control,
    name: 'productIds',
  })
  const watchedName = useWatch({
    control,
    name: 'name',
  })
  const { field: trackDlcField } = useController({ control, name: 'trackDlc' })
  const activeProducts = useMemo(
    () => (products ?? []).filter((product) => product.isActive),
    [products],
  )

  const conflictingProducts = useMemo(() => {
    const selectedProductIds = watchedProductIds ?? []
    if (selectedProductIds.length === 0) {
      return []
    }

    return activeProducts
      .filter((product) => selectedProductIds.includes(product.id))
      .filter((product) => {
        if (!product.internalCategoryId) {
          return false
        }
        if (!updateMode) {
          return true
        }
        return product.internalCategoryId !== internalCategoryId
      })
      .map((product) => ({
        productLabel: product.label,
        categoryLabel:
          internalCategories?.find((category) => category.id === product.internalCategoryId)
            ?.name ?? 'Catégorie inconnue',
      }))
  }, [activeProducts, watchedProductIds, updateMode, internalCategoryId, internalCategories])

  const internalCategoryLabelById = useMemo(
    () =>
      (internalCategories ?? []).reduce(
        (acc, category) => {
          acc[category.id] = category.name
          return acc
        },
        {} as Record<number, string>,
      ),
    [internalCategories],
  )

  const productItems = useMemo<ProductDualListItem[]>(
    () =>
      activeProducts.map((product) => {
        const groupId = product.internalCategoryId ?? 0
        return {
          id: product.id,
          label: product.label,
          groupId,
          groupLabel: internalCategoryLabelById[groupId] ?? 'AUTRE',
        }
      }),
    [activeProducts, internalCategoryLabelById],
  )

  // Fill the form with initial data once the data is fetched
  useEffect(() => {
    if (data) {
      setValue('name', data.name)
      setValue('trackDlc', data.trackDlc)
      setValue('productIds', data.products)
    }
  }, [setValue, data])

  useEffect(() => {
    if (errors.name?.type === 'manual') {
      clearErrors('name')
    }
  }, [watchedName, errors.name?.type, clearErrors])

  const navigateBack = () => {
    queryClient.invalidateQueries({ queryKey: ['products'] })
    navigate({ search: { tab: 'internal' }, to: '/categories' })
  }

  const setDuplicateNameError = () => {
    setError('name', {
      type: 'manual',
      message: DUPLICATE_INTERNAL_CATEGORY_NAME_MESSAGE,
    })
  }

  const isDuplicateNameError = (error: unknown) => {
    const errorData =
      error instanceof AxiosError && error.response?.data && typeof error.response.data === 'object'
        ? (error.response.data as Record<string, unknown>)
        : null

    const candidates = [errorData?.code, errorData?.message, errorData?.error]
      .filter((value): value is string => typeof value === 'string')
      .map((value) => value.toLowerCase())

    return candidates.some(
      (value) =>
        value.includes('23505') ||
        value.includes('duplicate key') ||
        value.includes('internal_categories_name_unique'),
    )
  }

  const hasDuplicateInternalCategoryName = (name: string) => {
    const normalizedName = normalizeCategoryName(name)
    if (!normalizedName) {
      return false
    }

    return (internalCategories ?? []).some((category) => {
      if (updateMode && category.id === internalCategoryId) {
        return false
      }

      return normalizeCategoryName(category.name) === normalizedName
    })
  }

  const { mutate: upload, isPending: isUploading } = useMutation({
    mutationFn: createInternalCategory,
    onSuccess: navigateBack,
    onError: (error: unknown) => {
      if (!updateMode && isDuplicateNameError(error)) {
        setDuplicateNameError()
      }
    },
  })

  const { mutate: update, isPending: isUpdating } = useMutation({
    mutationFn: (newData: InternalCategoryForm) =>
      updateInternalCategory(internalCategoryId ?? 0, newData),
    onSuccess: navigateBack,
  })

  const onSubmit = (formData: InternalCategoryForm) => {
    if (!updateMode && hasDuplicateInternalCategoryName(formData.name)) {
      setDuplicateNameError()
      return
    }

    clearErrors('name')

    if (updateMode) {
      update(formData)
      return
    }

    upload(formData)
  }

  return (
    <Page sx={{ margin: 'auto', maxWidth: 'min(1200px, 95vw)', width: '100%' }}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div style={{ alignItems: 'center', display: 'flex' }}>
          <IconButton onClick={navigateBack}>
            <ArrowLeft
              style={{
                color: ThemeColors.BLACK,
                height: '24px',
                width: '24px',
              }}
            />
          </IconButton>
          <Typography style={{ ...fonts.pageSubtitle, marginLeft: '16px' }} flexGrow={1}>
            {updateMode
              ? `${isUserAdmin ? 'Modifier' : 'Voir'} une catégorie interne`
              : `Ajouter une catégorie interne`}
          </Typography>

          <Button
            variant="outlined"
            sx={{ marginRight: '16px', ...ButtonOverrides.blackOutlined }}
            onClick={navigateBack}
          >
            Annuler
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={!isUserAdmin ? true : isUploading || isUpdating}
          >
            {updateMode ? 'Modifier' : 'Ajouter'}
          </Button>
        </div>
        <LoadingPaper
          sx={{ marginTop: '40px', padding: '24px', paddingBottom: '40px' }}
          loading={isLoading && updateMode}
        >
          <Typography mb="8px" style={fonts.inputHeader}>
            Nom
          </Typography>
          <TextFieldRhf
            variant="outlined"
            label="Nom de la catégorie"
            control={control}
            trigger={trigger}
            name="name"
            fullWidth
          />

          <div className="flex items-center gap-3 mt-4">
            <Switch
              id="track-dlc"
              checked={trackDlcField.value === true}
              onCheckedChange={(checked) => trackDlcField.onChange(checked)}
              disabled={!isUserAdmin}
            />
            <Label htmlFor="track-dlc" className="cursor-pointer">
              Inclure cette catégorie dans le suivi DLC hebdomadaire
            </Label>
          </div>

          <Typography mb="8px" mt="12px" style={fonts.inputHeader}>
            Produits
          </Typography>
          <ProductDualListSelectorRhf
            control={control}
            name="productIds"
            items={productItems}
            disabled={!isUserAdmin}
          />

          {conflictingProducts.length > 0 && (
            <>
              <Typography
                mt="8px"
                style={{ ...fonts.informationSubText, color: ThemeColors.ORANGE }}
              >
                Attention : les produits ci-dessous sont déjà dans une autre catégorie et seront
                déplacés.
              </Typography>
              <Typography
                component="ul"
                mt="6px"
                sx={{
                  ...fonts.informationSubText,
                  color: ThemeColors.ORANGE,
                  paddingLeft: '20px',
                  margin: 0,
                }}
              >
                {conflictingProducts.map((product) => (
                  <li key={`${product.productLabel}-${product.categoryLabel}`}>
                    {product.productLabel} — {product.categoryLabel}
                  </li>
                ))}
              </Typography>
            </>
          )}
        </LoadingPaper>
      </form>
    </Page>
  )
}
